import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Grid, Avatar, Button,
  Accordion, AccordionSummary, AccordionDetails, List, ListItem,
  ListItemText, Tabs, Tab, Card, CardContent, useTheme, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WorkIcon from '@mui/icons-material/Work';
import GavelIcon from '@mui/icons-material/Gavel';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ArticleIcon from '@mui/icons-material/Article';
import MapIcon from '@mui/icons-material/Map';
import DescriptionIcon from '@mui/icons-material/Description';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupsIcon from '@mui/icons-material/Groups';
import { searchParliamentarians, searchRecordsOfService, searchMinistryRecords, searchShadowMinistryRecords, searchParties, searchElectorates, getParliamentarianElections } from '../services/api';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import { formatParliamentarianName } from '../utils/formatName';
import ServiceTimeline from './ServiceTimeline';
import CommitteeTimeline from './CommitteeTimeline';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';
import useTableSort from '../hooks/useTableSort';

// Sort keys for the Electoral history tab (#283).
const ELECTION_SORT_KEYS = {
  Year: (e) => Number(e.Year) || 0,
  Chamber: (e) => e.chamber || '',
  Seat: (e) => e.Division || e.State || '',
  Party: (e) => e.Party || '',
  '2CP %': (e) => (e.TCP_pct == null ? -1 : e.TCP_pct),
  Swing: (e) => (e.Swing == null ? -999 : e.Swing),
  'Turnout %': (e) => (e.Turnout == null ? -1 : e.Turnout),
};

// ROSTypeID mapping for Records of Service
const ROS_TYPE_NAMES = {
  1: 'Acting Ministries',
  2: 'Committee Service',  // Committee data - displayed in separate tab
  3: 'Conferences, Delegations and Visits',
  4: 'Electorate',
  5: 'Ministerial Appointments',
  6: 'Parliamentary Appointments',
  7: 'Parliamentary Party Positions',
  8: 'Parliamentary Positions',
  9: 'Parliamentary Service',
  10: 'Party Positions',
  11: 'Parties Represented',
  12: 'State/Territory Government Service',
  13: 'Decorations',
  14: 'Honours',
  15: 'Local Government Service',
  16: 'Military Service',
  17: 'Miscellaneous Information',
  18: 'Occupations',
  19: 'Other Positions',
  20: 'Publications',
  21: 'Qualifications',
  22: 'Shadow Ministry Appointments',
  999: 'Committee Service Records'
};

// Category groupings for cleaner display
const ROS_CATEGORIES = {
  'Parliamentary': [5, 6, 7, 8, 9, 1, 22],
  'Political': [10, 11],
  'Prior Experience': [12, 15, 16, 18, 19],
  'Education & Publications': [20, 21],
  'Honours & Recognition': [13, 14],
  'Other': [3, 4, 17, 999]
};

// TabPanel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Stat card component for quick facts
function StatCard({ icon, label, value, color = 'primary' }) {
  const theme = useTheme();
  if (!value) return null;

  return (
    <Card
      variant="outlined"
      sx={{
        flex: '1 1 140px',
        minWidth: 140,
        bgcolor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.04),
        borderColor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.2),
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: theme.palette[color]?.main || theme.palette.primary.main,
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.15)}`
        }
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {React.cloneElement(icon, {
            sx: { fontSize: 18, color: `${color}.main` }
          })}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

// Related record link button - supports both horizontal and vertical modes
function RelatedRecordButton({ to, icon, label, count, vertical = false }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      variant="text"
      size="small"
      startIcon={icon}
      sx={{
        textTransform: 'none',
        borderRadius: 2,
        px: 1.5,
        py: 0.75,
        justifyContent: 'flex-start',
        width: vertical ? '100%' : 'auto',
        color: 'text.primary',
        fontSize: '0.85rem',
        '&:hover': {
          bgcolor: 'action.hover'
        }
      }}
    >
      {label}
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
        />
      )}
    </Button>
  );
}

export default function ParliamentarianDetail() {
  const { phid } = useParams();
  const theme = useTheme();
  const [parliamentarian, setParliamentarian] = useState(null);
  const [recordsOfService, setRecordsOfService] = useState([]);
  const [ministryRecords, setMinistryRecords] = useState([]);
  const [shadowMinistryRecords, setShadowMinistryRecords] = useState([]);
  const [partyColors, setPartyColors] = useState({});
  const [partyId, setPartyId] = useState(null);
  const [electorateId, setElectorateId] = useState(null);
  const [electionHistory, setElectionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  // Unconditional hook (stable order) over the electoral-history rows.
  const electionSort = useTableSort(electionHistory, (e, col) => ELECTION_SORT_KEYS[col](e));

  // Electoral history (#283) — best-effort; empty for pre-Phid / historic MPs.
  useEffect(() => {
    let active = true;
    setElectionHistory([]);
    (async () => {
      try {
        const res = await getParliamentarianElections(phid);
        if (active) setElectionHistory(res.elections || []);
      } catch (e) {
        if (active) setElectionHistory([]);
      }
    })();
    return () => { active = false; };
  }, [phid]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch parliamentarian by PHID
        const parlData = await searchParliamentarians(`PHID:"${phid}"`, 1, 1);
        if (parlData.hits && parlData.hits.length > 0) {
          setParliamentarian(parlData.hits[0]);

          // Fetch related records of service
          const rosData = await searchRecordsOfService(`PHID:"${phid}"`, 1, 100);
          setRecordsOfService(rosData.hits || []);

          // Fetch ministry records
          const ministryData = await searchMinistryRecords(`PHID:"${phid}"`, 1, 100);
          setMinistryRecords(ministryData.hits || []);

          // Fetch shadow ministry records
          const shadowData = await searchShadowMinistryRecords(`PHID:"${phid}"`, 1, 100);
          setShadowMinistryRecords(shadowData.hits || []);

          // Fetch party colors for the timeline + resolve the current
          // parliamentarian's pID so the "View Party" related-record button
          // can link to /parties/<pID>. Reuses the same single fetch (#269).
          const partyData = await searchParties('*', 1, 100);
          if (partyData.hits) {
            const colors = {};
            const currentPartyName = parlData.hits[0].Party;
            partyData.hits.forEach(party => {
              if (party.Name && party.Colour) {
                colors[party.Name] = party.Colour;
              }
              if (party.Abbrev && party.Colour) {
                colors[party.Abbrev] = party.Colour;
              }
              if (currentPartyName && party.Name === currentPartyName) {
                setPartyId(party.pID);
              }
            });
            setPartyColors(colors);
          }

          // Fetch electorate ID if parliamentarian has an electorate
          const parlElectorate = parlData.hits[0].Electorate;
          if (parlElectorate) {
            try {
              // Try exact match first, then fallback to simple search
              let electorateData = await searchElectorates(`Electorate:"${parlElectorate}"`, 1, 1);
              if (!electorateData.hits || electorateData.hits.length === 0) {
                // Fallback: search without field prefix
                electorateData = await searchElectorates(parlElectorate, 1, 1);
              }
              if (electorateData.hits && electorateData.hits.length > 0) {
                setElectorateId(electorateData.hits[0].EID);
              }
            } catch (elecErr) {
              console.error('Error fetching electorate ID:', elecErr);
            }
          }
        } else {
          setError('Parliamentarian not found');
        }
      } catch (err) {
        console.error('Error fetching parliamentarian:', err);
        setError('Failed to load parliamentarian data');
      }
      setLoading(false);
    };
    fetchData();
  }, [phid]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const InfoRow = ({ label, value, link, multiline = false }) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    const isArray = Array.isArray(value);
    const displayValue = isArray && !multiline ? value.join(', ') : value;

    return (
      <Box sx={{ display: 'flex', py: 0.75 }}>
        <Typography
          variant="body2"
          sx={{ width: 140, flexShrink: 0, color: 'text.secondary', fontWeight: 500 }}
        >
          {label}
        </Typography>
        {link ? (
          <Button
            component={RouterLink}
            to={link}
            size="small"
            sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 400 }}
          >
            {displayValue}
          </Button>
        ) : isArray && multiline ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {value.map((item, idx) => (
              <Typography key={idx} variant="body2">{item}</Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body2">{displayValue}</Typography>
        )}
      </Box>
    );
  };

  // Extract committee records (ROSTypeID 2) separately
  const committeeRecords = recordsOfService.filter(r => r.ROSTypeID === 2);

  // Filter out records that are shown in other tabs:
  // - ROSTypeID 1: Acting Ministries (shown in Executive Roles tab)
  // - ROSTypeID 2: Committee Service (shown in Committees tab)
  // - ROSTypeID 4: Electorate (shown in Career tab)
  // - ROSTypeID 5: Ministerial Appointments (shown in Executive Roles tab)
  // - ROSTypeID 6: Parliamentary Appointments (shown in Career tab)
  // - ROSTypeID 7: Parliamentary Party Positions (shown in Career tab)
  // - ROSTypeID 8: Parliamentary Positions (shown in Career tab)
  // - ROSTypeID 9: Parliamentary Service (shown in Service Timeline)
  // - ROSTypeID 10: Party Positions (shown in Career tab)
  // - ROSTypeID 11: Parties Represented (shown in Service Timeline Party Membership)
  // - ROSTypeID 13: Decorations (shown in Biography tab)
  // - ROSTypeID 14: Honours (shown in Biography tab)
  // - ROSTypeID 15: Local Government Service (shown in Biography tab)
  // - ROSTypeID 16: Military Service (shown in Biography tab)
  // - ROSTypeID 18: Occupations (shown in Biography tab)
  // - ROSTypeID 20: Publications (shown in Biography tab)
  // - ROSTypeID 21: Qualifications (shown in Biography tab)
  // - ROSTypeID 22: Shadow Ministry Appointments (shown in Executive Roles tab)
  const recordsTabExcludedTypes = [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 13, 14, 15, 16, 18, 20, 21, 22];
  const nonCommitteeRecords = recordsOfService.filter(r => !recordsTabExcludedTypes.includes(r.ROSTypeID));

  // Extract specific record types for display in other tabs
  const actingMinistryRecords = recordsOfService.filter(r => r.ROSTypeID === 1);
  const parliamentaryAppointmentRecords = recordsOfService.filter(r => r.ROSTypeID === 6);
  const parliamentaryPartyPositionRecords = recordsOfService.filter(r => r.ROSTypeID === 7);
  const parliamentaryPositionRecords = recordsOfService.filter(r => r.ROSTypeID === 8);
  const partyPositionRecords = recordsOfService.filter(r => r.ROSTypeID === 10);
  const decorationRecords = recordsOfService.filter(r => r.ROSTypeID === 13);
  const localGovtRecords = recordsOfService.filter(r => r.ROSTypeID === 15);
  const militaryRecords = recordsOfService.filter(r => r.ROSTypeID === 16);
  const publicationRecords = recordsOfService.filter(r => r.ROSTypeID === 20);

  // Group records of service by category (excluding committees)
  const groupedROS = nonCommitteeRecords.reduce((acc, record) => {
    const typeName = ROS_TYPE_NAMES[record.ROSTypeID] || `Type ${record.ROSTypeID}`;
    if (!acc[typeName]) acc[typeName] = [];
    acc[typeName].push(record);
    return acc;
  }, {});

  // Group by category for compact view
  const categorizedROS = {};
  Object.entries(ROS_CATEGORIES).forEach(([category, typeIds]) => {
    const records = typeIds.flatMap(id => {
      const typeName = ROS_TYPE_NAMES[id];
      return typeName && groupedROS[typeName] ? groupedROS[typeName] : [];
    });
    if (records.length > 0) {
      categorizedROS[category] = records;
    }
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
        <Button component={RouterLink} to="/parliamentarians" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Parliamentarians
        </Button>
      </Box>
    );
  }

  const hit = parliamentarian;

  // Determine chamber color
  const isSenator = hit.MPorSenator?.includes('Senator');
  const isMember = hit.MPorSenator?.includes('Member');
  const chamberColor = isSenator && !isMember ? '#c62828' : isMember && !isSenator ? '#2e7d32' : '#1a73e8';

  // Calculate service years
  const serviceYears = hit.ServiceHistory_Duration?.match(/(\d+)\s*years?/i)?.[1];

  // Determine chamber membership for conditional links
  const hasSenateService = hit.MPorSenator?.includes('Senator');
  const hasHouseService = hit.MPorSenator?.includes('Member');

  // Build combined electorate/state service history inline (not useMemo since we're after early returns)
  const combinedServiceHistory = (() => {
    const history = [];

    // Add House electorate service
    (hit.ElectorateService || []).forEach(es => {
      history.push({
        name: es.Electorate,
        state: es.State,
        chamber: 'House',
        start: es.ServiceStart,
        end: es.ServiceEnd,
        isSenate: false
      });
    });

    // Add Senate state service from records of service
    (recordsOfService || [])
      .filter(r => r.ROSTypeID === 9 && r.MpOrSenator === 'Senator')
      .forEach(r => {
        history.push({
          name: r.Value1, // State name
          state: r.Value1,
          chamber: 'Senate',
          start: r.DateStart1,
          end: r.DateEnd1,
          isSenate: true
        });
      });

    // Sort by start date (most recent first)
    history.sort((a, b) => new Date(b.start) - new Date(a.start));

    return history;
  })();

  // Sidebar content for Related Records
  const RelatedRecordsSidebar = () => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 80,
        maxHeight: 'calc(100vh - 100px)',
        overflow: 'auto'
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.95rem' }}>
        Related Records
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <RelatedRecordButton
          to={`/hansard?q=speaker_id:"${phid}"`}
          icon={<RecordVoiceOverIcon fontSize="small" />}
          label="Hansard Speeches"
          vertical
        />
        {hasSenateService && (
          <RelatedRecordButton
            to={`/questions?q=senator:"${hit.DisplayName || hit.FamilyName}"`}
            icon={<QuestionAnswerIcon fontSize="small" />}
            label="Senate Estimates"
            vertical
          />
        )}
        {hasSenateService && (
          <RelatedRecordButton
            to={`/senateqon?q=asked_by_id:"${phid}"`}
            icon={<QuestionAnswerIcon fontSize="small" />}
            label="Senate QoN"
            vertical
          />
        )}
        {hasHouseService && (
          <RelatedRecordButton
            to={`/houseqiw?q=asked_by_id:"${phid}"`}
            icon={<QuestionAnswerIcon fontSize="small" />}
            label="House QiW"
            vertical
          />
        )}
        <RelatedRecordButton
          to={`/bills?q=movers:*${encodeURIComponent(hit.FamilyName)}*`}
          icon={<DescriptionIcon fontSize="small" />}
          label="Moved/Sponsored Bills"
          vertical
        />
        <RelatedRecordButton
          to={`/divisions?q=votes_aye:*${encodeURIComponent(hit.FamilyName)}* OR votes_no:*${encodeURIComponent(hit.FamilyName)}*`}
          icon={<HowToVoteIcon fontSize="small" />}
          label="Division Votes"
          vertical
        />
        {hit.Electorate && (
          <RelatedRecordButton
            to={electorateId ? `/electorates/${electorateId}` : `/electorates?q=Electorate:"${encodeURIComponent(hit.Electorate)}"`}
            icon={<MapIcon fontSize="small" />}
            label="View Electorate"
            vertical
          />
        )}
        {hit.Party && partyId && (
          <RelatedRecordButton
            to={`/parties/${partyId}`}
            icon={<GroupsIcon fontSize="small" />}
            label="View Party"
            vertical
          />
        )}
      </Box>
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          PHID: <code style={{ background: '#f5f5f5', padding: '2px 4px', borderRadius: 4, fontSize: '0.7rem' }}>{phid}</code>
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/parliamentarians"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Parliamentarians
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar - Related Records */}
        <Grid item xs={12} md={3} sx={{ order: { xs: 2, md: 1 } }}>
          <RelatedRecordsSidebar />
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9} sx={{ order: { xs: 1, md: 2 } }}>
          {/* Hero Section */}
          <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(chamberColor, 0.08)} 0%, ${alpha(chamberColor, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(chamberColor, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
          {/* Photo */}
          <Avatar
            src={getParliamentarianPhotoUrl(phid)}
            sx={{
              width: { xs: 100, sm: 140 },
              height: { xs: 100, sm: 140 },
              fontSize: '2.5rem',
              bgcolor: chamberColor,
              border: '4px solid white',
              boxShadow: `0 4px 20px ${alpha(chamberColor, 0.3)}`,
              alignSelf: { xs: 'center', sm: 'flex-start' }
            }}
          >
            {hit.GivenName?.[0]}{hit.FamilyName?.[0]}
          </Avatar>

          {/* Info */}
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            {/* Name and titles */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: chamberColor,
                mb: 0.5,
                fontSize: { xs: '1.75rem', sm: '2.125rem' }
              }}
            >
              {formatParliamentarianName(hit)}
            </Typography>

            {/* Role subtitle */}
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 400 }}>
              {hit.Party}
              {hit.Electorate && ` • ${hit.Electorate}`}
              {hit.SenateState && ` • ${hit.SenateState}`}
              {` • ${hit.State}`}
            </Typography>

            {/* Status chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' }, mb: 2 }}>
              {hit.MPorSenator?.map(role => (
                <Chip
                  key={role}
                  label={role === 'Member' ? 'House of Representatives' : 'Senate'}
                  size="small"
                  sx={{
                    bgcolor: role === 'Member' ? '#2e7d32' : '#c62828',
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              ))}
              {hit.InCurrentParliament === 'True' && (
                <Chip
                  label="Current Parliamentarian"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {hit.FirstNations && (
                <Chip label="First Nations" size="small" color="secondary" />
              )}
              {hit.Gender && (
                <Chip label={hit.Gender} size="small" variant="outlined" />
              )}
            </Box>

            {/* Quick stats */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <StatCard
                icon={<TimelineIcon />}
                label="Service"
                value={hit.ServiceHistory_Duration || (serviceYears ? `${serviceYears} years` : null)}
                color="primary"
              />
              <StatCard
                icon={<HowToVoteIcon />}
                label="Parliaments"
                value={hit.RepresentedParliaments?.length > 0
                  ? `${hit.RepresentedParliaments.length} (${Math.min(...hit.RepresentedParliaments)}-${Math.max(...hit.RepresentedParliaments)})`
                  : null}
                color="info"
              />
              {ministryRecords.length > 0 && (
                <StatCard
                  icon={<WorkIcon />}
                  label="Ministries"
                  value={`${ministryRecords.length} appointment${ministryRecords.length !== 1 ? 's' : ''}`}
                  color="warning"
                />
              )}
              {shadowMinistryRecords.length > 0 && (
                <StatCard
                  icon={<GavelIcon />}
                  label="Shadow"
                  value={`${shadowMinistryRecords.length} appointment${shadowMinistryRecords.length !== 1 ? 's' : ''}`}
                  color="secondary"
                />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Service Timeline */}
      {(hit.ServiceHistory_Start || hit.ElectorateService?.length > 0) && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            Parliamentary Service Timeline
          </Typography>
          <ServiceTimeline
            serviceRecords={recordsOfService}
            overallStart={hit.ServiceHistory_Start}
            overallEnd={hit.ServiceHistory_End}
            mpOrSenator={hit.MPorSenator}
            electorateService={hit.ElectorateService}
            ministryRecords={ministryRecords}
            shadowMinistryRecords={shadowMinistryRecords}
            partyColors={partyColors}
          />
        </Paper>
      )}

      {/* Tabbed Content */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48
            }
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="Biography" />
          <Tab icon={<AccountBalanceIcon />} iconPosition="start" label="Career" />
          {(ministryRecords.length > 0 || shadowMinistryRecords.length > 0) && (
            <Tab icon={<WorkIcon />} iconPosition="start" label="Executive Roles" />
          )}
          {committeeRecords.length > 0 && (
            <Tab icon={<GroupsIcon />} iconPosition="start" label={`Committees (${committeeRecords.length})`} />
          )}
          {nonCommitteeRecords.length > 0 && (
            <Tab icon={<ArticleIcon />} iconPosition="start" label={`Records (${nonCommitteeRecords.length})`} />
          )}
          {electionHistory.length > 0 && (
            <Tab icon={<HowToVoteIcon />} iconPosition="start" label={`Elections (${electionHistory.length})`} />
          )}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Biography Tab */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Personal Details
            </Typography>
            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 3 }}>
              <InfoRow label="Full Name" value={`${hit.GivenName}${hit.MiddleNames ? ' ' + hit.MiddleNames : ''} ${hit.FamilyName}`} />
              <InfoRow label="Preferred Name" value={hit.PreferredName} />
              <InfoRow label="Date of Birth" value={formatDate(hit.DateOfBirth)} />
              <InfoRow label="Age" value={hit.Age_String} />
              <InfoRow label="Place of Birth" value={hit.PlaceOfBirth} />
              <InfoRow label="Country of Birth" value={hit.CountryOfBirth} />
              <InfoRow label="State of Birth" value={hit.StateOfBirth} />
              <InfoRow label="Marital Status" value={hit.MaritalStatus} />
              {hit.DateOfDeath && <InfoRow label="Date of Death" value={formatDate(hit.DateOfDeath)} />}
              {hit.PlaceOfDeath && <InfoRow label="Place of Death" value={hit.PlaceOfDeath} />}
              {hit.FirstNations && <InfoRow label="First Nations" value={hit.FirstNationsText || 'Yes'} />}
            </Box>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Education & Background
            </Typography>
            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
              <InfoRow label="Secondary School" value={hit.SecondarySchool} />
              <InfoRow label="Qualifications" value={hit.Qualifications} multiline />
              <InfoRow label="Occupations" value={hit.Occupations} multiline />
            </Box>

            {(hit.Honours?.length > 0 || hit.ParliamentaryPositions?.length > 0) && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                  Honours & Positions
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                  <InfoRow label="Honours" value={hit.Honours} />
                  <InfoRow label="Parliamentary Positions" value={hit.ParliamentaryPositions} />
                </Box>
              </>
            )}

            {/* Decorations from Records of Service */}
            {decorationRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                  Decorations ({decorationRecords.length})
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                  {decorationRecords.map((record, idx) => (
                    <Box
                      key={record.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < decorationRecords.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label="Decoration"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'warning.main', color: 'white' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {record.Value1 || record.Text1}
                        </Typography>
                        {record.Value2 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.Value2}
                          </Typography>
                        )}
                      </Box>
                      {record.DateStart1 && record.DateStart1 !== '1900-01-01' && (
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 80 }}>
                          {new Date(record.DateStart1).getFullYear()}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Military Service from Records of Service */}
            {militaryRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                  Military Service ({militaryRecords.length})
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                  {militaryRecords.map((record, idx) => (
                    <Box
                      key={record.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < militaryRecords.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label="Military"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'success.main', color: 'white' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {record.Value1 || record.Text1}
                        </Typography>
                        {record.Value2 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.Value2}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                        {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Local Government Service from Records of Service */}
            {localGovtRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                  Local Government Service ({localGovtRecords.length})
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                  {localGovtRecords.map((record, idx) => (
                    <Box
                      key={record.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < localGovtRecords.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label="Local Govt"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {record.Value1 || record.Text1}
                        </Typography>
                        {record.Value2 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.Value2}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                        {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Publications from Records of Service */}
            {publicationRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                  Publications ({publicationRecords.length})
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                  {publicationRecords.map((record, idx) => {
                    const publicationTitle = record.Value1 || record.Text1;
                    const authorName = hit.DisplayName || `${hit.GivenName} ${hit.FamilyName}`;
                    const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(`"${publicationTitle}" ${authorName}`)}`;

                    return (
                      <Box
                        key={record.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          py: 1.5,
                          borderBottom: idx < publicationRecords.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label="Publication"
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                          <Button
                            href={googleSearchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600, textAlign: 'left' }}
                          >
                            {publicationTitle}
                          </Button>
                          {record.Value2 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {record.Value2}
                            </Typography>
                          )}
                        </Box>
                        {record.DateStart1 && record.DateStart1 !== '1900-01-01' && (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 80 }}>
                            {new Date(record.DateStart1).getFullYear()}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </TabPanel>

          {/* Career Tab */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Parliamentary Service
            </Typography>
            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 3 }}>
              <InfoRow label="Chamber" value={hit.MPorSenator} />
              <InfoRow label="Party" value={hit.Party} />
              <InfoRow label="State" value={hit.State} />
              {hit.Electorate && (
                <InfoRow
                  label="Electorate"
                  value={hit.Electorate}
                  link={electorateId ? `/electorates/${electorateId}` : `/electorates?q=Electorate:"${encodeURIComponent(hit.Electorate)}"`}
                />
              )}
              {hit.SenateState && <InfoRow label="Senate State" value={hit.SenateState} />}
              <InfoRow label="Service Start" value={formatDate(hit.ServiceHistory_Start)} />
              <InfoRow label="Service End" value={formatDate(hit.ServiceHistory_End) || (hit.InCurrentParliament === 'True' ? 'Present' : null)} />
              <InfoRow label="Duration" value={hit.ServiceHistory_Duration} />
              <InfoRow
                label="Parliaments"
                value={hit.RepresentedParliaments?.map(p =>
                  `${p}${p === 1 ? 'st' : p === 2 ? 'nd' : p === 3 ? 'rd' : 'th'}`
                ).join(', ')}
              />
            </Box>

            {/* Electorate/State Service History */}
            {combinedServiceHistory.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Electorate/State History ({combinedServiceHistory.length})
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 3 }}>
                  {combinedServiceHistory.map((service, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < combinedServiceHistory.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label={service.chamber}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: service.isSenate ? '#c62828' : '#2e7d32',
                              color: 'white'
                            }}
                          />
                        </Box>
                        {service.isSenate ? (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {service.name}
                          </Typography>
                        ) : (
                          <Button
                            component={RouterLink}
                            to={service.name === hit.Electorate && electorateId
                              ? `/electorates/${electorateId}`
                              : `/electorates?q=Electorate:"${encodeURIComponent(service.name)}"`}
                            size="small"
                            sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600 }}
                          >
                            {service.name}
                          </Button>
                        )}
                        {!service.isSenate && service.state && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {service.state}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                        {formatDate(service.start)} — {formatDate(service.end) || 'Present'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Parties Represented */}
            {hit.PartiesRepresented?.length > 1 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Parties Represented
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {hit.PartiesRepresented.map((party, idx) => (
                    <Chip key={idx} label={party} variant="outlined" size="small" />
                  ))}
                </Box>
              </>
            )}

            {/* Parliamentary Appointments from ROS (Type 6) */}
            {parliamentaryAppointmentRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Parliamentary Appointments ({parliamentaryAppointmentRecords.length})
                </Typography>
                <Box sx={{ bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2, p: 2, border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.2), mb: 3 }}>
                  {parliamentaryAppointmentRecords.map((record, idx) => (
                    <Box
                      key={record.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < parliamentaryAppointmentRecords.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label="Parliament"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'info.main', color: 'white' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {record.Value1}
                        </Typography>
                        {record.Value2 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.Value2}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                        {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Parliamentary Party Positions from ROS (Type 7) */}
            {parliamentaryPartyPositionRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Parliamentary Party Positions ({parliamentaryPartyPositionRecords.length})
                </Typography>
                <Box sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2, p: 2, border: '1px solid', borderColor: alpha(theme.palette.secondary.main, 0.2), mb: 3 }}>
                  {parliamentaryPartyPositionRecords.map((record, idx) => (
                    <Box
                      key={record.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < parliamentaryPartyPositionRecords.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label="Party"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'secondary.main', color: 'white' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {record.Value1}
                        </Typography>
                        {record.Value2 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.Value2}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                        {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Party Positions from ROS (Type 10) */}
            {partyPositionRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Party Positions ({partyPositionRecords.length})
                </Typography>
                <Box sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2, p: 2, border: '1px solid', borderColor: alpha(theme.palette.secondary.main, 0.2), mb: 3 }}>
                  {partyPositionRecords.map((record, idx) => (
                    <Box
                      key={record.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < partyPositionRecords.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label="Party Position"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'secondary.main', color: 'white' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {record.Value1}
                        </Typography>
                        {record.Value2 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.Value2}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                        {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Parliamentary Positions from ROS (Type 8) */}
            {parliamentaryPositionRecords.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Parliamentary Positions ({parliamentaryPositionRecords.length})
                </Typography>
                <Box sx={{ bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2, p: 2, border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.2) }}>
                  {parliamentaryPositionRecords.map((record, idx) => (
                    <Box
                      key={record.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: idx < parliamentaryPositionRecords.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label="Position"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'info.main', color: 'white' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {record.Value1}
                        </Typography>
                        {record.Value2 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.Value2}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                        {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </TabPanel>

          {/* Executive Roles Tab */}
          {(ministryRecords.length > 0 || shadowMinistryRecords.length > 0) && (
            <TabPanel value={activeTab} index={2}>
              {/* Ministry Records */}
              {ministryRecords.length > 0 && (() => {
                // Sort by start date ascending
                const sortedRecords = [...ministryRecords].sort((a, b) =>
                  new Date(a.RDateStart) - new Date(b.RDateStart)
                );

                // Consolidate records: if someone is both Minister and Cabinet Minister for the same
                // position in overlapping periods, keep only the Cabinet Minister entry
                const consolidatedRecords = sortedRecords.reduce((acc, record) => {
                  // Build position string from Entity (and Prep if present)
                  const position = record.Entity
                    ? (record.Prep ? `${record.Prep} ${record.Entity}` : record.Entity)
                    : record.Position || record.Role;

                  // Check if we already have a record with same position in same ministry with overlapping dates
                  const existingIdx = acc.findIndex(r => {
                    const rPosition = r.Entity
                      ? (r.Prep ? `${r.Prep} ${r.Entity}` : r.Entity)
                      : r.Position || r.Role;

                    // Same position and same ministry
                    if (rPosition !== position || r.Ministry !== record.Ministry) return false;

                    // Check for overlapping dates
                    const rStart = new Date(r.RDateStart);
                    const rEnd = r.RDateEnd ? new Date(r.RDateEnd) : new Date();
                    const recStart = new Date(record.RDateStart);
                    const recEnd = record.RDateEnd ? new Date(record.RDateEnd) : new Date();

                    return rStart <= recEnd && recStart <= rEnd;
                  });

                  if (existingIdx >= 0) {
                    // If existing is Minister and new is Cabinet Minister, replace
                    // Cabinet Minister is a higher role
                    const existing = acc[existingIdx];
                    if (existing.Role === 'Minister' && record.Role === 'Cabinet Minister') {
                      acc[existingIdx] = record;
                    }
                    // Otherwise keep the existing one (it's either Cabinet Minister already or the same role)
                  } else {
                    acc.push(record);
                  }

                  return acc;
                }, []);

                return (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                      Ministry Appointments ({consolidatedRecords.length})
                    </Typography>
                    <Box sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2, p: 2, border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.2), mb: 3 }}>
                      {consolidatedRecords.map((record, idx) => {
                        // Build full position string with "Minister for..." prefix
                        let position;
                        if (record.Entity) {
                          const prep = record.Prep || 'for';
                          position = `${record.Role} ${prep} ${record.Entity}`;
                        } else {
                          position = record.Position || record.Role;
                        }

                        return (
                          <Box
                            key={record.id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              py: 1.5,
                              borderBottom: idx < consolidatedRecords.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip
                                  label={record.Role}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: record.Role === 'Cabinet Minister' ? 'warning.dark' : 'warning.main',
                                    color: 'white'
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {position}
                              </Typography>
                              <Button
                                component={RouterLink}
                                to={`/ministries/${record.MID}`}
                                size="small"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', mt: 0.5 }}
                              >
                                {record.Ministry}
                              </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                              {formatDate(record.RDateStart)} — {formatDate(record.RDateEnd) || 'Present'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                );
              })()}

              {/* Shadow Ministry Records */}
              {shadowMinistryRecords.length > 0 && (() => {
                // Sort by start date ascending
                const sortedShadowRecords = [...shadowMinistryRecords].sort((a, b) =>
                  new Date(a.RDateStart) - new Date(b.RDateStart)
                );

                return (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                      Shadow Ministry Appointments ({sortedShadowRecords.length})
                    </Typography>
                    <Box sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2, p: 2, border: '1px solid', borderColor: alpha(theme.palette.secondary.main, 0.2) }}>
                      {sortedShadowRecords.map((record, idx) => {
                        // Build full position string with "Shadow Minister for..." prefix
                        let position;
                        if (record.Entity) {
                          const prep = record.Prep || 'for';
                          position = `${record.Role} ${prep} ${record.Entity}`;
                        } else {
                          position = record.Position || record.Role;
                        }

                        return (
                          <Box
                            key={record.id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              py: 1.5,
                              borderBottom: idx < sortedShadowRecords.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip
                                  label={record.Role}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'secondary.main', color: 'white' }}
                                />
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {position}
                              </Typography>
                              <Button
                                component={RouterLink}
                                to={`/shadowministries/${record.SMID}`}
                                size="small"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', mt: 0.5 }}
                              >
                                {record.Ministry}
                              </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                              {formatDate(record.RDateStart)} — {formatDate(record.RDateEnd) || 'Present'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                );
              })()}

              {/* Acting Ministries from ROS (Type 1) */}
              {actingMinistryRecords.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                    Acting Ministries ({actingMinistryRecords.length})
                  </Typography>
                  <Box sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2, p: 2, border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.2) }}>
                    {actingMinistryRecords.map((record, idx) => (
                      <Box
                        key={record.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          py: 1.5,
                          borderBottom: idx < actingMinistryRecords.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label="Acting"
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'warning.light', color: 'warning.dark' }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {record.Value1 || record.Text1}
                          </Typography>
                          {record.Value2 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {record.Value2}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                          {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </TabPanel>
          )}

          {/* Committees Tab */}
          {committeeRecords.length > 0 && (
            <TabPanel value={activeTab} index={(ministryRecords.length > 0 || shadowMinistryRecords.length > 0) ? 3 : 2}>
              {/* Committee Timeline */}
              <CommitteeTimeline
                committeeRecords={committeeRecords}
                overallStart={hit.ServiceHistory_Start}
                overallEnd={hit.ServiceHistory_End}
              />

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Committee Service ({committeeRecords.length} records)
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                {committeeRecords.map((record, idx) => (
                  <Box
                    key={record.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      py: 1.5,
                      borderBottom: idx < committeeRecords.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={record.Value1 || 'Committee'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Button
                        component={RouterLink}
                        to={`/committees?q=CommitteeName:"${encodeURIComponent(record.Value2 || '')}"`}
                        size="small"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600, textAlign: 'left' }}
                      >
                        {record.Value2 || record.Value1}
                      </Button>
                      {record.Value3 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Role: {record.Value3}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 180 }}>
                      {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </TabPanel>
          )}

          {/* Records of Service Tab */}
          {nonCommitteeRecords.length > 0 && (
            <TabPanel value={activeTab} index={
              2 +
              ((ministryRecords.length > 0 || shadowMinistryRecords.length > 0) ? 1 : 0) +
              (committeeRecords.length > 0 ? 1 : 0)
            }>
              {Object.entries(groupedROS).map(([typeName, records]) => (
                <Accordion
                  key={typeName}
                  defaultExpanded={records.length <= 5}
                  sx={{
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    mb: 1,
                    '&.Mui-expanded': { mb: 1 }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'grey.50', borderRadius: '8px' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 500 }}>{typeName}</Typography>
                      <Chip label={records.length} size="small" sx={{ height: 20 }} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <List dense disablePadding>
                      {records.map((record, idx) => (
                        <ListItem
                          key={record.id}
                          sx={{
                            py: 0.75,
                            px: 0,
                            borderBottom: idx < records.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                {record.Value1 || record.Text1}
                                {record.Value2 && ` — ${record.Value2}`}
                                {record.Value3 && ` — ${record.Value3}`}
                              </Typography>
                            }
                            secondary={
                              (record.DateStart1 || record.DateEnd1) && (
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(record.DateStart1)} — {formatDate(record.DateEnd1) || 'Present'}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </TabPanel>
          )}

          {/* Electoral history (#283) */}
          {electionHistory.length > 0 && (
            <TabPanel value={activeTab} index={
              2 +
              ((ministryRecords.length > 0 || shadowMinistryRecords.length > 0) ? 1 : 0) +
              (committeeRecords.length > 0 ? 1 : 0) +
              (nonCommitteeRecords.length > 0 ? 1 : 0)
            }>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Elections won
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each election this member won, with two-party-preferred result, swing and turnout
                where available. Sourced from the Parliamentary Handbook election data.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Year', 'Chamber', 'Seat', 'Party', '2CP %', 'Swing', 'Turnout %'].map((c) => (
                        <TableCell key={c} sx={{ fontWeight: 600 }}
                          sortDirection={electionSort.orderBy === c ? electionSort.order : false}>
                          <TableSortLabel active={electionSort.orderBy === c}
                            direction={electionSort.orderBy === c ? electionSort.order : 'asc'}
                            onClick={() => electionSort.handleSort(c)}>{c}</TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {electionSort.sortedRows.map((e, i) => (
                      <TableRow key={`${e.Year}-${e.Division || e.State}-${i}`} hover>
                        <TableCell>
                          {e.election_id ? (
                            <RouterLink to={`/elections/${e.election_id}`} style={{ color: 'inherit' }}>
                              {e.Year}
                            </RouterLink>
                          ) : e.Year}
                        </TableCell>
                        <TableCell>{e.chamber === 'senate' ? 'Senate' : 'House'}</TableCell>
                        <TableCell>{e.Division || e.State || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {e.PartyColour && (
                              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: e.PartyColour, flexShrink: 0 }} />
                            )}
                            {e.Party || '—'}
                          </Box>
                        </TableCell>
                        <TableCell>{e.TCP_pct == null ? '—' : `${e.TCP_pct}%`}</TableCell>
                        <TableCell>{e.Swing == null ? '—' : `${e.Swing > 0 ? '+' : ''}${e.Swing}`}</TableCell>
                        <TableCell>{e.Turnout == null ? '—' : `${e.Turnout}%`}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          )}
        </Box>
      </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
